import React, { Fragment, useState, useEffect } from 'react';
import { graphql, Link, withPrefix } from 'gatsby';
import styled from 'styled-components';
import { ThemeProvider } from 'styled-components';
import { theme } from 'common/theme/app';
import { ResetCSS } from 'common/assets/css/style';
import { GlobalStyle } from 'containers/App/app.style';
import Seo from 'components/seo';
import BlogSidebar from 'components/BlogSidebar';
import { isRunningInAppWebView } from 'common/components/AppModeDetector';
import AppAwareLink from 'components/BlogSidebar/AppAwareLink';
import ContentPage from 'layouts/ContentPage';
import SiteLayout from 'layouts/SiteLayout';

const BlogPageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 2.5rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
  
  /* Tighten spacing for app views */
  .app-view & {
    padding: 0 1rem 40px;
  }
`;

const MainContent = styled.div`
  width: 100%;
  min-width: 0;
`;

const BlogPost = styled.article`
  margin-bottom: 20px;
  padding: 24px;
  border: 1px solid ${props => props.theme.colors.borderColor};
  border-radius: 16px;
  background: ${props => props.theme.colors.surface};
  
  &:last-child {
    border-bottom: none;
  }
  
  /* Tighten spacing for app views */
  .app-view & {
    margin-bottom: 2rem;
    padding-bottom: 2rem;
  }
`;

const PostMeta = styled.div`
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textColor};
  opacity: 0.8;
`;

const PostDate = styled.time`
  font-weight: 500;
`;

const PostCategory = styled.span`
  display: inline-block;
  text-transform: uppercase;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.secondaryDark};
`;

const PostTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  font-weight: 700;
  line-height: 1.3;
  color: ${props => props.theme.colors.headingColor};
  
  /* Smaller font for app views */
  .app-view & {
    font-size: 1.4rem;
    margin-bottom: 0.5rem;
  }
  
  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const PostImageContainer = styled.div`
  margin-bottom: 1.5rem;
  border-radius: 12px;
  overflow: hidden;
  max-height: 300px;
  
  /* Tighten spacing for app views */
  .app-view & {
    margin-bottom: 1rem;
    border-radius: 8px;
  }
  
  img {
    width: 100%;
    height: 300px;
    display: block;
    transition: transform 0.3s ease;
    object-fit: cover;
    
    /* Allow flexible height in app view */
    .app-view & {
      height: 200px;
    }
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const PostExcerpt = styled.div`
  margin-bottom: 1.2rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.textColor};
  
  /* Adjust text for app views */
  .app-view & {
    margin-bottom: 0.8rem;
    font-size: 0.95rem;
  }
`;

const ReadMoreButton = styled(Link)`
  display: inline-block;
  padding: 8px 20px;
  background: ${props => props.theme.colors.primary};
  color: white;
  text-decoration: none;
  border-radius: 999px;
  font-weight: 500;
  font-size: 0.9rem;
  transition: background 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primaryHover || '#222'};
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3rem;
  gap: 0.5rem;
`;

const PaginationLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.surfaceMuted};
  color: ${props => props.active ? 'white' : props.theme.colors.textColor};
  text-decoration: none;
  font-weight: ${props => props.active ? '600' : '400'};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? props.theme.colors.primary : '#e0e0e0'};
  }
`;

const PaginationEllipsis = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
`;

const PaginationNavButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 15px;
  height: 40px;
  border-radius: 999px;
  background: ${props => props.theme.colors.surfaceMuted};
  color: ${props => props.theme.colors.textColor};
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e0e0e0;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BlogListTemplate = ({ data, pageContext, hideHeaderFooter = false, location }) => {
  // Use state for the combined value, not just the app detection
  const [shouldHideHeaderFooter, setShouldHideHeaderFooter] = useState(false);
  
  // Check URL parameters for app mode - highest priority
  const isAppMode = location && location.search ? 
    new URLSearchParams(location.search).get('app') === 'true' : false;
  
  useEffect(() => {
    // If app=true parameter is present, apply immediately to avoid flicker
    if (isAppMode) {
      setShouldHideHeaderFooter(true);
      return;
    }
    
    // Only check for WebView detection if not already determined by URL parameter
    if (typeof window !== 'undefined') {
      setShouldHideHeaderFooter(hideHeaderFooter || isRunningInAppWebView());
    }
  }, [hideHeaderFooter, isAppMode]);
  
  const { currentPage, numPages } = pageContext;
  const posts = data.allMarkdownRemark.edges;
  const isFirst = currentPage === 1;
  const isLast = currentPage === numPages;
  const prevPage = currentPage - 1 === 1 ? '/blog' : `/blog/page/${currentPage - 1}`;
  const nextPage = `/blog/page/${currentPage + 1}`;
  
  // Process image path to handle both static and CMS paths
  const processImagePath = (path) => {
    if (!path) return null;
    
    // If path starts with '/', it's relative to the static folder
    if (typeof path === 'string' && path.startsWith('/')) {
      return withPrefix(path);
    }
    
    return path;
  };
  
  // Function to render pagination numbers with ellipsis for many pages
  const renderPaginationNumbers = () => {
    const pageNumbers = [];
    
    // Always show first page
    pageNumbers.push(
      <PaginationLink 
        key="1" 
        to="/blog" 
        active={currentPage === 1}
      >
        1
      </PaginationLink>
    );
    
    // Add ellipsis if needed
    if (currentPage > 3) {
      pageNumbers.push(<PaginationEllipsis key="ellipsis-1">...</PaginationEllipsis>);
    }
    
    // Add pages around current page
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(numPages - 1, currentPage + 1); i++) {
      pageNumbers.push(
        <PaginationLink 
          key={i} 
          to={i === 1 ? '/blog' : `/blog/page/${i}`} 
          active={currentPage === i}
        >
          {i}
        </PaginationLink>
      );
    }
    
    // Add ellipsis if needed
    if (currentPage < numPages - 2) {
      pageNumbers.push(<PaginationEllipsis key="ellipsis-2">...</PaginationEllipsis>);
    }
    
    // Always show last page if there is more than one page
    if (numPages > 1) {
      pageNumbers.push(
        <PaginationLink 
          key={numPages} 
          to={`/blog/page/${numPages}`} 
          active={currentPage === numPages}
        >
          {numPages}
        </PaginationLink>
      );
    }
    
    return pageNumbers;
  };

  // Content section that can be used in both web and mobile contexts
  const BlogContent = () => (
    <BlogPageContainer>
      <MainContent>
        {posts.map(({ node }) => {
          const title = node.frontmatter.title || node.fields.slug;
          const thumbnailPath = processImagePath(node.frontmatter.thumbnail);
          const formattedDate = node.frontmatter.date;
          
          return (
            <BlogPost key={node.fields.slug}>
              {!shouldHideHeaderFooter && (
                <PostCategory>
                  {node.frontmatter.category || 'Blog'}
                </PostCategory>
              )}
              
              <PostTitle>
                <AppAwareLink to={node.fields.slug}>{title}</AppAwareLink>
              </PostTitle>
              
              {!shouldHideHeaderFooter && (
                <PostMeta>
                  <PostDate>{formattedDate}</PostDate>
                </PostMeta>
              )}
              
              {thumbnailPath && (
                <PostImageContainer>
                  <AppAwareLink to={node.fields.slug}>
                    <img 
                      src={thumbnailPath} 
                      alt={title}
                    />
                  </AppAwareLink>
                </PostImageContainer>
              )}
              
              <PostExcerpt>
                {node.frontmatter.description || node.excerpt}
              </PostExcerpt>
              
              {!shouldHideHeaderFooter && (
                <ReadMoreButton to={node.fields.slug}>
                  Read more
                </ReadMoreButton>
              )}
            </BlogPost>
          );
        })}
        
        {numPages > 1 && !shouldHideHeaderFooter && (
          <Pagination>
            {!isFirst && (
              <PaginationLink 
                as={AppAwareLink}
                to={prevPage} 
                rel="prev"
              >
                ← Previous
              </PaginationLink>
            )}
            
            {renderPaginationNumbers()}
            
            {!isLast && (
              <PaginationLink 
                as={AppAwareLink}
                to={nextPage} 
                rel="next"
              >
                Next →
              </PaginationLink>
            )}
          </Pagination>
        )}
      </MainContent>
      
      {!shouldHideHeaderFooter && <BlogSidebar />}
    </BlogPageContainer>
  );

  // For mobile app usage, we can return just the content without wrapper
  if (shouldHideHeaderFooter) {
    return (
      <ThemeProvider theme={theme}>
        <Fragment>
          <ResetCSS />
          <GlobalStyle />
          <div className="app-view" style={{ padding: 0, margin: 0 }}>
            <BlogContent />
          </div>
        </Fragment>
      </ThemeProvider>
    );
  }

  // For web usage, include the full layout
  return (
    <SiteLayout>
        <Seo 
          title="Blog" 
          description="Latest articles and updates" 
          isBlogPage={true}
        />
        
        <ContentPage
          title="Blog"
          description="Our latest news, updates, and stories about pets"
          eyebrow="Furbuz journal"
          width="1200px"
        >
          <BlogContent />
        </ContentPage>
    </SiteLayout>
  );
};

// Export the content component for reuse in mobile apps
export const BlogListContent = ({ data, pageContext }) => {
  return <BlogListTemplate data={data} pageContext={pageContext} hideHeaderFooter={true} />;
};

export const query = graphql`
  query blogListQuery($skip: Int!, $limit: Int!) {
    allMarkdownRemark(
      sort: { fields: [frontmatter___date], order: DESC }
      limit: $limit
      skip: $skip
    ) {
      edges {
        node {
          excerpt(pruneLength: 120)
          fields {
            slug
          }
          frontmatter {
            date(formatString: "DD.MMMM.YYYY")
            title
            description
            thumbnail
            category
            tags
          }
        }
      }
    }
  }
`;

export default BlogListTemplate; 